// VendorSubscriptionsSection.js — one collapsible row per connected app, under
// the note's own sections. Closed: logo, app name, when it was last answered,
// and a refresh icon that re-runs just that app (a clinician action, contract
// §3). Open: the app's questions rendered straight from its Flex++ structure —
// generic, recursive, nothing app-specific — with the latest answers inline.
import { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import useVendorStore from '../../utils/vendorApps/useVendorStore';

const TYPE_WORDS = { boolean: 'yes / no', number: 'number', integer: 'whole number', string: 'text', 'string[]': 'list', 'number[]': 'list of numbers' };
const isLeaf = (node) => node && typeof node === 'object' && !Array.isArray(node) && Object.keys(node).length > 0 && Object.keys(node).every((k) => k.startsWith('_'));
const isEmpty = (s) => s == null || (typeof s === 'string' && !s.trim()) || (typeof s === 'object' && !Array.isArray(s) && Object.keys(s).length === 0);
const humanize = (key) => String(key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

/** What a leaf is asking, and what kind of answer it takes — from the structure alone. */
export function leafHint(node) {
  if (typeof node === 'string') {
    const m = node.match(/^\s*(boolean|number|integer|string\[\]|number\[\]|string)\s*(?:[—–-]+\s*)?(.*)$/i);
    return { ask: (m ? m[2] : node).trim(), type: m ? TYPE_WORDS[m[1].toLowerCase()] : 'text' };
  }
  if (isLeaf(node)) {
    const type = Array.isArray(node._choices)
      ? `${node._multiple === true ? 'any of' : 'one of'}: ${node._choices.join(', ')}`
      : TYPE_WORDS[node._type] || 'text';
    return { ask: (node._description || '').trim(), type };
  }
  return { ask: '', type: '' };
}

export function formatAnswer(v) {
  if (v === undefined) return null;
  if (v === null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (Array.isArray(v)) return v.length ? v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join('; ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null);

function Leaf({ label, node, value }) {
  const { ask, type } = leafHint(node);
  const answer = formatAnswer(value);
  return (
    <div className="d-flex align-items-baseline small py-1" style={{ borderBottom: '1px solid #f1f3f5' }}>
      <div style={{ flex: '1 1 55%' }}>
        {label && <span>{label}</span>}
        {ask && label && <span className="text-muted"> · </span>}
        {ask && <span className={label ? 'text-muted' : ''}>{ask}</span>}
        {type && <span className="text-muted"> ({type})</span>}
      </div>
      <div style={{ flex: '0 0 45%' }} className="text-right">
        {answer === null ? (
          <span className="text-muted font-italic">awaiting Comprehend</span>
        ) : answer === '—' ? (
          <span className="text-muted">not mentioned</span>
        ) : (
          <span className="badge badge-pill" style={{ background: '#2dce89', color: '#fff', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'normal', textAlign: 'left' }}>
            {answer}
          </span>
        )}
      </div>
    </div>
  );
}

/** Generic renderer: walks the vendor's structure and shows the answer at each leaf. */
function FlexTree({ structure, answers, depth = 0 }) {
  if (isEmpty(structure)) {
    // Summary mode: an empty structure → one plain-text answer.
    return <p className="small mb-0">{answers === undefined ? <span className="text-muted">Summary of the visit, once answered.</span> : formatAnswer(answers)}</p>;
  }
  if (typeof structure === 'string' || isLeaf(structure)) return <Leaf node={structure} value={answers} />;
  if (Array.isArray(structure)) {
    const rows = Array.isArray(answers) ? answers : [];
    const proto = structure[0];
    if (!rows.length) {
      return (
        <div className="small text-muted">
          list · one row per item{proto && typeof proto === 'object' ? ` with ${Object.keys(proto).filter((k) => !k.startsWith('_')).map(humanize).join(', ')}` : ''}
          {answers !== undefined ? ' · none' : ''}
        </div>
      );
    }
    return (
      <div>
        {rows.map((row, i) => (
          <div key={i} className="border-left pl-2 mb-1" style={{ borderLeftWidth: 2 }}>
            <FlexTree structure={proto} answers={row} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  const keys = Object.keys(structure).filter((k) => !k.startsWith('_'));
  return (
    <div>
      {structure._description && <div className="small text-muted font-italic mb-1">{structure._description}</div>}
      {keys.map((k) => {
        const child = structure[k];
        const value = answers && typeof answers === 'object' && !Array.isArray(answers) ? answers[k] : undefined;
        if (typeof child === 'string' || isLeaf(child)) return <Leaf key={k} label={humanize(k)} node={child} value={value} />;
        return (
          <div key={k} className="mb-2">
            <div className="small font-weight-bold" style={{ marginBottom: 2 }}>
              {humanize(k)}
            </div>
            <div className="pl-3">
              <FlexTree structure={child} answers={value} depth={depth + 1} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AppRow({ store, app, saved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const subs = Array.from(store.subsFor(app.id).values());
  const status = store.statusFor(app.id);
  // Live answers first; else what was persisted for this structure (survives a reload).
  const resultFor = (sub) => sub.lastResult || (saved?.[sub.hash] ? { answers: saved[sub.hash].answers, answeredAt: saved[sub.hash].answeredAt } : null);
  const answered = subs.some((s) => resultFor(s));
  const lastAnsweredAt = status.lastEvaluatedAt || subs.map((s) => resultFor(s)?.answeredAt).filter(Boolean).sort().pop() || null;
  const spinning = busy || Boolean(status.evaluating);

  // The refresh icon spins until this app reports a new evaluation (or 20 s pass).
  useEffect(() => {
    setBusy(false);
  }, [status.lastEvaluatedAt]);
  useEffect(() => {
    if (!busy) return undefined;
    const t = setTimeout(() => setBusy(false), 20000);
    return () => clearTimeout(t);
  }, [busy]);

  const refresh = (e) => {
    e.stopPropagation();
    if (!store.patient || busy) return;
    setBusy(true);
    store.requestEvaluate('manual', app.id);
  };

  return (
    <div className="outlineContainer" style={{ borderLeft: `3px solid ${app.color || '#adb5bd'}` }} data-vendor-app-section={app.id}>
      <div className="flex-container" style={{ display: 'flex', alignItems: 'center' }}>
        <strong className="dropdown-label" onClick={() => setOpen(!open)} style={{ flex: 1, marginRight: '10px', display: 'flex', alignItems: 'center' }}>
          <div className={`arrow ${open ? 'open' : ''}`}></div>
          {app.icon && <img src={app.icon} alt="" width="18" height="18" className="rounded mr-2" style={{ objectFit: 'cover' }} />}
          <div style={{ fontSize: '0.85rem' }}>{app.name}</div>
          <small className="text-muted ml-2" style={{ fontWeight: 400 }}>
            {spinning ? 'answering…' : lastAnsweredAt ? `answered ${fmtTime(lastAnsweredAt)}` : answered ? 'answered' : 'not answered yet — press Comprehend'}
          </small>
        </strong>
        {/* Same control as a note section's refresh: reactstrap Button, fa-refresh at 18px, spins while working. */}
        <Button
          size="med"
          className="vendor-app-refresh"
          aria-label={`Refresh ${app.name}`}
          title={store.patient ? `Answer ${app.name}'s questions against this visit now` : 'Open a visit first'}
          disabled={!store.patient}
          onClick={refresh}
          style={answered && !spinning ? { backgroundColor: '#85df9d' } : undefined}
        >
          <i className={`fa fa-refresh${spinning ? ' fa-spin' : ''}`} style={{ fontSize: '18px' }}></i>
        </Button>
      </div>
      {open && (
        <div className="pt-2">
          <div className="d-flex small text-uppercase text-muted pb-1" style={{ letterSpacing: '.04em', fontSize: '0.7rem', borderBottom: '1px solid #e9ecef' }}>
            <div style={{ flex: '1 1 55%' }}>What {app.name} asked</div>
            <div style={{ flex: '0 0 45%' }} className="text-right">
              {answered ? `Comprehend's answer${lastAnsweredAt ? ` · ${fmtTime(lastAnsweredAt)}` : ''}` : 'Answer'}
            </div>
          </div>
          {subs.map((sub, i) => (
            <div key={i} className={i ? 'mt-3' : ''}>
              <FlexTree structure={sub.structure} answers={resultFor(sub) ? resultFor(sub).answers : undefined} />
            </div>
          ))}
          {!answered && <div className="small text-muted mt-2">Nothing answered yet — press Comprehend, or the refresh on this row.</div>}
        </div>
      )}
    </div>
  );
}

export default function VendorSubscriptionsSection({ appContext }) {
  const store = useVendorStore();
  const apps = store.apps.filter((a) => a.enabled && store.subsFor(a.id).size > 0);
  if (apps.length === 0) return null;
  return (
    <div id="vendor-app-sections">
      {apps.map((app) => (
        <AppRow key={app.id} store={store} app={app} saved={appContext?.[app.id]?.answers} />
      ))}
    </div>
  );
}
